import axios from 'axios'
import { clientDetails } from 'stores/helpers/clientDetailsData/smava'
import { getEnv } from 'utils/env'

export type FetchEkomiOptions = {
  signal?: AbortSignal
  urlOverride?: string // for testing or tenant-specific overrides
}

/**
 * Fetches the eKomi HTML snippet from smava endpoint and returns it as raw HTML string.
 * Use inside a component by setting the value via dangerouslySetInnerHTML.
 */
export async function fetchEkomiHtml(
  options: FetchEkomiOptions = {}
): Promise<string> {
  const { signal, urlOverride } = options
  const env = getEnv('REACT_APP_ENV')

  const resolvedUrl = (() => {
    if (urlOverride) return urlOverride
    if (!env) return undefined
    const details = clientDetails[env as keyof typeof clientDetails]
    return details?.urls?.ekomiApi
  })()

  if (!resolvedUrl) {
    throw new Error('Ekomi URL not configured for current environment')
  }

  try {
    const res = await axios.get<string>(resolvedUrl, {
      responseType: 'text',
      withCredentials: true,
      headers: { Accept: 'text/html, */*' },
      signal
    })

    return res.data
  } catch (err: any) {
    const status = err?.response?.status
    const statusText = err?.response?.statusText
    const body: string | undefined = err?.response?.data
    throw new Error(
      `Failed to fetch eKomi component: ${status ?? 'unknown'} ${statusText ?? ''} ${body ? `- ${String(body).slice(0, 200)}` : ''}`
    )
  }
}

/**
 * Convenience helper for React dangerouslySetInnerHTML.
 * <div dangerouslySetInnerHTML={toInnerHtml(html)} />
 */
export function toInnerHtml(html: string): { __html: string } {
  return { __html: html }
}
